import { iceServersFor } from './calls.js'

/**
 * The relay's TURN credentials, kept fresh.
 *
 * A call between two phones on mobile networks goes through TURN or not at
 * all: carrier NAT lets nothing in from outside. The credentials come from
 * the server (/api/turn) and last twelve hours. The apps used to ask once, at
 * start — and the Android app starts once and runs for days, so by the next
 * day every call it made or took was on expired credentials. Worse, a phone
 * that started without a network (at boot, say) never had any. The desktop
 * app never asked at all.
 *
 * So this is asked before every call: what it has, if that is under an hour
 * old; the server again otherwise. When the server cannot be reached, the
 * last credentials still go out (they may well be valid), and the next call
 * asks again.
 */
export function iceSource ({ url, host = 'papi.olshub.xyz', ttl = 60 * 60 * 1000, timeout = 5000, fetchImpl = globalThis.fetch } = {}) {
  const base = String(url || '').replace(/\/+$/, '')
  let cached = null
  let at = 0
  let pending = null

  async function ask () {
    if (!base || typeof fetchImpl !== 'function') return iceServersFor({ host })
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timer = controller ? setTimeout(() => controller.abort(), timeout) : null
    try {
      const response = await fetchImpl(base + '/api/turn', {
        cache: 'no-store',
        signal: controller ? controller.signal : undefined
      })
      if (!response.ok) throw new Error('HTTP ' + response.status)
      const body = await response.json()
      cached = iceServersFor({ host, username: body.username || null, credential: body.credential || null })
      at = Date.now()
      return cached
    } catch {
      return cached || iceServersFor({ host })
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  return function iceServers () {
    if (cached && Date.now() - at < ttl) return Promise.resolve(cached)
    if (!pending) pending = ask().finally(() => { pending = null })
    return pending
  }
}
